import {Component, type OnInit, ChangeDetectionStrategy} from '@angular/core';
import {type ReceiptError, ReceiptSettingsService} from "../services/receipt-settings.service";
import type {ReceiptConfiguration} from "../models/receipt-configuration";
import {ConfirmationService, MerchantChangeService, MerchantService, NotificationService} from "@odin/odin-core";
import {Subscription} from "rxjs";
import {AccessGuardService, RoleGuardService} from "@odin/odin-authentication";
import { Router } from '@angular/router';

@Component({
  standalone: false,
  selector: 'odin-receipt-configuration-page',
  templateUrl: './receipt-configuration-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './receipt-configuration-page.component.scss'
})
export class ReceiptConfigurationPageComponent implements OnInit {

  public smsEnabled: boolean = false;
  public configLoading: boolean = true;
  public changeLoading: boolean = false;
  public receiptConfiguration: ReceiptConfiguration | null = null;
  public originalReceiptConfiguration: string;
  public emails: string[] = [];
  public errors: ReceiptError[] = [];
  private merchantChangeSub: Subscription;


  receiptManagePermission: boolean;

  public backToSettings(): void {
    this.router.navigate(['/settings']);
  }

  constructor(
    private accessService: AccessGuardService,
    private receiptService: ReceiptSettingsService,
    private confirmationService: ConfirmationService,
    private notificationService: NotificationService,
    private merchantChangeService: MerchantChangeService,
    private merchantService: MerchantService,
    private router: Router,
    private roleGuardService: RoleGuardService,
  ) {

      this.receiptManagePermission = this.roleGuardService.hasPermission('settings:Manage');

      if (!this.GrantChecker()) {
        this.router.navigate(['/dashboard']);
        return;
      }

      this.merchantChangeService.onChange('receipt configuration', window.location.href, (buid: string) => {
        setTimeout(() => this.fetchConfig(), 500);
      });

      this.merchantChangeSub = this.merchantChangeService.merchantChangeEvent.subscribe(() => {
        if (!this.GrantChecker()) {
          this.accessService.boot(
            'User does not have access to Receipt Configuration. Please contact an administrator',
          );
        }
      });
    }

  public GrantChecker(): boolean {
    return this.accessService.hasNewGrant('txn:Receipts') || this.roleGuardService.hasPermission('settings:View')
  }

  ngOnInit() {
    this.fetchConfig();
  }

  private fetchConfig(): void {
    this.configLoading = true;
    this.receiptService.getConfig().subscribe((config: ReceiptConfiguration) => {
      this.setupConfig(config);
      this.configLoading = false;
    }, (error) => {
      this.configLoading = false;
      this.receiptConfiguration = null;
      // 404 means no configuration exists yet, which the page handles with "Create New Configuration".
      if (error?.status !== 404) {
        this.notificationService.ErrorSmallDialog("Failed to load receipt configuration", error);
      }
    })
  }

  private setupConfig(config: ReceiptConfiguration, forcedOriginalConfig?: string): void {
    this.originalReceiptConfiguration = JSON.stringify(config);
    // Only E-Mail is supported as a send type
    config.customerDestination = 0;
    config.merchantDestination = 0;
    this.receiptConfiguration = config;
    if (forcedOriginalConfig !== undefined) this.originalReceiptConfiguration = forcedOriginalConfig;
    this.emails = [...config.emailConfiguration.merchantEmailAddresses];
    this.changeLoading = false;
    this.configLoading = false;
  }

  public hasChanged(): boolean {
    if (this.receiptConfiguration === null) return false;
    const config = this.receiptService.validate(this.receiptConfiguration, this.smsEnabled);
    if (!config.valid) {
      this.errors = config.errors;
      return false;
    } else {
      this.errors = [];
    }
    return JSON.stringify(this.receiptConfiguration) !== this.originalReceiptConfiguration;
  }

  public AddEmail(): void {
    this.emails.push('');
    if (this.receiptConfiguration && this.receiptConfiguration.emailConfiguration) {
      this.receiptConfiguration.emailConfiguration.merchantEmailAddresses = [...this.emails];
    }
  }

  public RemoveEmail(index: number): void {
    this.emails.splice(index, 1);
    if (this.receiptConfiguration && this.receiptConfiguration.emailConfiguration) {
      this.receiptConfiguration.emailConfiguration.merchantEmailAddresses = [...this.emails];
    }
  }

  public UpdateEmail(index: number, email: string): void {
    this.emails[index] = email;
    if (this.receiptConfiguration && this.receiptConfiguration.emailConfiguration) {
      this.receiptConfiguration.emailConfiguration.merchantEmailAddresses = [...this.emails];
    }
  }

  public trackEmail(index: number, email: string): number {
    return index;
  }

  public GetError(id: string): string {
    const error = this.errors.find(e => e.id === id);
    if (error) {
      return error.error;
    }
    return '';
  }

  public createNewConfiguration(): void {
    const merchantRef = this.merchantService.getCurrentBuid();
    if (merchantRef === null) return;
    const newConfig = {
      businessUnitId: merchantRef.id,
      enabledFor: 2,
      customerDestination: 0,
      merchantDestination: 0,
      tradingName: merchantRef.name,
      emailConfiguration: {
        merchantEmailTemplateId: 1,
        merchantEmailAddresses: [""],
        merchantEmailSubject: "",

        customerEmailSubject: "",
        customerEmailTemplateId: 1,
      },
      smsConfiguration: {
        customerSmsTemplateId: 1,
        merchantSmsTemplateId: 1,
        merchantPhoneNumber: "",
      }
    } as ReceiptConfiguration;
    this.setupConfig(newConfig, 'original');
  }

  public saveNewConfiguration(): void {
    if (this.receiptConfiguration === null) return;
    this.changeLoading = true;
    this.receiptService.createConfig(this.receiptConfiguration).subscribe((config: ReceiptConfiguration) => {
      this.fetchConfig();
    }, (error) => {
      this.changeLoading = false;
      this.notificationService.ErrorSmallDialog("Failed to create new receipt configuration", error);
    });
  }

  public updateConfiguration(): void {
    if (this.receiptConfiguration === null) return;
    if (this.originalReceiptConfiguration === 'original') {
      this.saveNewConfiguration();
      return;
    } else {
      this.changeLoading = true;
      this.receiptService.updateConfig(this.receiptConfiguration).subscribe((config: ReceiptConfiguration) => {
        this.fetchConfig();
      }, (error) => {
        this.changeLoading = false;
        this.notificationService.ErrorSmallDialog("Failed to update receipt configuration", error);
      });
    }
  }

  public deleteConfiguration(): void {
    if (this.receiptConfiguration === null) return;
    this.confirmationService.ConfirmDecline("Delete receipt configuration", "Are you sure you want to delete this configuration?", "Yes", "Cancel", "warn", "primary").subscribe((res: boolean) => {
      if (!res) return;
      this.changeLoading = true;
      this.receiptService.deleteConfig().subscribe(() => {
        this.receiptConfiguration = null;
        this.originalReceiptConfiguration = '';
        this.changeLoading = false;
        this.fetchConfig();
      }, (error) => {
        this.changeLoading = false;
        this.notificationService.ErrorSmallDialog("Failed to delete receipt configuration", error);
      });
    });
  }
}
